/**
 * User button dropdown — shows avatar/email and logout action.
 */

import { useState, useRef, useEffect } from 'react'
import { LogOut, Settings, Users, Sparkles } from 'lucide-react'
import { supabase } from '@/lib/supabaseClient'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { useSync } from '@/hooks/useSync'

export const UserButton = () => {
  const darkMode = useEnergyStore((s) => s.darkMode)
  const userEmail = useEnergyStore((s) => s.userEmail)
  const isAuthenticated = useEnergyStore((s) => s.isAuthenticated)
  const subscriptionTier = useEnergyStore((s) => s.subscriptionTier)
  const setAuthModalOpen = useEnergyStore((s) => s.setAuthModalOpen)
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { pushToCloud } = useSync()

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleLogout = () => {
    useEnergyStore.getState().openConfirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      confirmText: 'Sign Out',
      isDestructive: true,
      onConfirm: async () => {
        try {
          await pushToCloud()
        } catch (e) {
          console.error('Error syncing before sign out:', e)
        }
        await supabase.auth.signOut()
      },
    })
    setTimeout(() => {
      setIsOpen(false)
    }, 100)
  }

  const initials = (() => {
    if (!userEmail) return 'GU'
    const parts = userEmail.split('@')
    const local = parts[0] ?? ''
    return local
      .split(/[._-]/)
      .filter((p) => p.length > 0)
      .map((p) => p.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  })()

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-colors ${
          darkMode
            ? 'bg-white/5 hover:bg-white/10 text-slate-300'
            : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
        }`}
      >
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black ${
            darkMode
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-emerald-100 text-emerald-700'
          }`}
        >
          {initials}
        </div>
        <span className="text-xs font-bold hidden sm:block max-w-[120px] truncate">
          {userEmail?.split('@')[0] || 'Guest'}
        </span>
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-56 rounded-2xl border shadow-xl overflow-hidden animate-section z-50 ${
            darkMode
              ? 'bg-slate-800 border-white/10'
              : 'bg-white border-slate-200'
          }`}
        >
          <div
            className={`p-4 border-b ${
              darkMode ? 'border-white/5' : 'border-slate-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {isAuthenticated ? 'Signed in as' : 'Browsing as'}
                </p>
                <p className={`text-[11px] mt-0.5 truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{userEmail || 'Guest User'}</p>
              </div>
              {isAuthenticated && (
                <div className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${
                  subscriptionTier === 'free' ? 'bg-slate-500/10 text-slate-500' :
                  subscriptionTier === 'pro' ? 'bg-emerald-500/10 text-emerald-500' :
                  'bg-amber-500/10 text-amber-500'
                }`}>
                  {subscriptionTier}
                </div>
              )}
            </div>
          </div>

          <div className="p-2">
            {isAuthenticated ? (
              <>
                {subscriptionTier === 'free' && (
                  <button
                    onClick={() => {
                      useEnergyStore.setState({ isPricingModalOpen: true })
                      setIsOpen(false)
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-black uppercase tracking-widest text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Upgrade to Pro
                  </button>
                )}
                <button
                  onClick={() => {
                    useEnergyStore.setState({ isHouseholdModalOpen: true })
                    setIsOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
                    darkMode
                      ? 'text-slate-300 hover:bg-white/5'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  Households
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
                    darkMode
                      ? 'text-slate-300 hover:bg-white/5'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Settings className="w-3.5 h-3.5" />
                  Account Settings
                </button>
                <button
                  onClick={handleLogout}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
                    darkMode
                      ? 'text-red-400 hover:bg-red-500/10'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setAuthModalOpen(true)
                  setIsOpen(false)
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-black uppercase tracking-widest text-emerald-500 hover:bg-emerald-500/10 transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Connect to Cloud
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
